'use client';

import { AlertCircle, Copy, RefreshCw } from 'lucide-react';
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getErrorMessage } from '@/lib/errors/get-error-message';
import { pushSweetLinkCliError } from '@/lib/sweetlink/cli-error-log';
import { toVoidPromise } from '@/lib/utils/async-handlers';
import { copyToClipboard } from '@/lib/utils/clipboard';

interface ErrorBoundaryState {
  error?: Error | undefined;
  hasError: boolean;
}

interface ErrorBoundaryProperties {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProperties, ErrorBoundaryState> {
  constructor(properties: ErrorBoundaryProperties) {
    super(properties);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    // Feed the SweetLink CLI crash console so the chrome-devtools MCP snapshot mirrors the UI error.
    pushSweetLinkCliError({
      type: 'error-boundary',
      message: error?.message ?? 'Unknown error',
      stack: error?.stack ?? null,
      source: errorInfo?.componentStack ?? null,
    });
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  override render(): React.ReactNode {
    if (this.state.hasError) {
      const error = this.state.error || new Error('An unknown error occurred');

      if (this.props.fallback) {
        return <this.props.fallback error={error} resetError={this.resetError} />;
      }

      return <DefaultErrorFallback error={error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProperties {
  error: Error;
  resetError: () => void;
}

export function DefaultErrorFallback({ error, resetError }: ErrorFallbackProperties) {
  const [copied, setCopied] = React.useState(false);
  const [isExpanded, setIsExpanded] = React.useState(true); // Show details by default

  const copyErrorToClipboard = toVoidPromise(async () => {
    const errorText = `Error: ${error.message || 'Unknown error'}\n\nStack: ${error.stack || 'No stack trace available'}`;
    try {
      await copyToClipboard(errorText, {
        onSuccess: () => {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        },
        onError: (error_) => {
          console.error('Failed to copy error to clipboard:', error_);
        },
      });
    } catch (error_) {
      console.error('Failed to copy error to clipboard:', error_);
      setCopied(false);
    }
  });

  // Get more lines from the stack trace
  const getFormattedError = () => {
    if (error.stack) {
      // Show first 10 lines of stack trace
      const lines = error.stack.split('\n');
      return lines.slice(0, 10).join('\n') + (lines.length > 10 ? '\n...' : '');
    }
    return error.message || 'Check the console for full errors and additional helpful warnings.';
  };

  return (
    <Card className="max-w-2xl mx-auto mt-8 bg-card border border-border/60">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          Something went wrong
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          An error occurred while loading this section
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm">
          <button
            type="button"
            onClick={() => {
              setIsExpanded((previous) => !previous);
            }}
            className="cursor-pointer font-medium text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <span
              className="transform transition-transform"
              style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
            >
              ▶
            </span>
            Error details
          </button>
          {isExpanded && (
            <pre className="mt-2 text-xs bg-muted/20 text-muted-foreground p-3 rounded-md overflow-auto border border-border/60 max-h-64">
              {getFormattedError()}
            </pre>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={resetError}
            variant="outline"
            className="flex-1 bg-card text-foreground hover:bg-card/80 border-border/60"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try again
          </Button>
          <Button
            onClick={copyErrorToClipboard}
            variant="outline"
            className="flex-1 bg-card text-foreground hover:bg-card/80 border-border/60"
            title="Copy error details to clipboard"
          >
            <Copy className="h-4 w-4 mr-2" />
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Hook for error handling in components
export function useErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null);

  const handleError = React.useCallback((error_: Error | string) => {
    const errorObject = typeof error_ === 'string' ? new Error(error_) : error_;
    setError(errorObject);
    console.error('Component error:', errorObject);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  return { error, handleError, clearError };
}

// Error fallback for specific features
export function FeatureErrorFallback({
  error,
  resetError,
  featureName,
}: ErrorFallbackProperties & { featureName: string }) {
  const [copied, setCopied] = React.useState(false);

  const copyErrorToClipboard = toVoidPromise(async () => {
    const errorText = `Feature: ${featureName}\nError: ${error.message || 'Unknown error'}\n\nStack: ${error.stack || 'No stack trace available'}`;
    try {
      await copyToClipboard(errorText, {
        onSuccess: () => {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        },
        onError: (error_) => {
          console.error('Failed to copy error to clipboard:', error_);
        },
      });
    } catch (error_) {
      console.error('Failed to copy error to clipboard:', error_);
      setCopied(false);
    }
  });

  return (
    <Card className="border-destructive/40 bg-destructive/10">
      <CardContent className="pt-6">
        <div className="text-center space-y-4">
          <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
          <div>
            <h3 className="font-semibold text-foreground">Failed to load {featureName}</h3>
            <p className="text-sm text-muted-foreground mt-1">{getErrorMessage(error)}</p>
          </div>
          <div className="flex gap-2 justify-center">
            <Button
              onClick={resetError}
              variant="outline"
              className="flex-1 bg-card text-foreground hover:bg-card/80 border-border/60"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            <Button
              onClick={copyErrorToClipboard}
              variant="outline"
              className="flex-1 bg-card text-foreground hover:bg-card/80 border-border/60"
              title="Copy error details to clipboard"
            >
              <Copy className="h-4 w-4 mr-2" />
              {copied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
